import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PostCard from "../components/PostCard";
import { useAuth } from "../contexts/AuthContext";
import { getPostById } from "../services/postService";
import { Post as PostType, Comment } from "../services/connections";
import { getUserPosts } from "../services/postService";

const PostPage: React.FC = () => {
  const { postId, ownerUserId } = useParams<{
    postId: string;
    ownerUserId?: string;
  }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [postData, setPostData] = useState<
    (PostType & {
      id: string;
      likesList: string[];
      CommentsList: Comment[];
    }) | null
  >(null);
  const [postOwnerId, setPostOwnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPost = useCallback(async () => {
    if (!postId) {
      setError("Invalid post link.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Preferred path: route includes owner ID, so fetch directly.
      if (ownerUserId) {
        const ownerPosts = await getUserPosts(ownerUserId, postId);
        if (ownerPosts.length > 0) {
          setPostData(ownerPosts[0]);
          setPostOwnerId(ownerUserId);
          return;
        }
      }

      // Backward compatibility: old /post/:postId links.
      // First try current user's posts (works for own posts).
      if (currentUser?.uid) {
        const ownPosts = await getUserPosts(currentUser.uid, postId);
        if (ownPosts.length > 0) {
          setPostData(ownPosts[0]);
          setPostOwnerId(currentUser.uid);
          return;
        }
      }

      const result = await getPostById(postId);
      if (!result) {
        setError("Post not found.");
        setPostData(null);
        setPostOwnerId(null);
        return;
      }

      setPostData(result.post);
      setPostOwnerId(result.userId);
    } catch (err) {
      console.error("Error loading shared post:", err);
      setError("Failed to load post.");
    } finally {
      setLoading(false);
    }
  }, [postId, ownerUserId, currentUser?.uid]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  return (
    <div className="min-h-screen bg-base-200 py-6">
      <div className="max-w-2xl mx-auto px-3 sm:px-4">
        {loading ? (
          <div className="bg-base-100 rounded-lg shadow p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-base-300 rounded w-1/3"></div>
              <div className="h-64 bg-base-300 rounded"></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-base-100 rounded-lg shadow p-6">
            <div className="bg-red-500/10 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
              <div className="mt-3 flex gap-3">
                <button
                  onClick={fetchPost}
                  className="text-sm text-red-700 hover:text-red-800 underline"
                >
                  Try again
                </button>
                <button
                  onClick={() => navigate("/feed")}
                  className="text-sm text-base-content/70 hover:text-base-content underline"
                >
                  Back to feed
                </button>
              </div>
            </div>
          </div>
        ) : postData && postOwnerId ? (
          <PostCard
            post={postData}
            userId={postOwnerId}
            currentUserId={currentUser?.uid}
            showOwnerActions={currentUser?.uid === postOwnerId}
            onPostUpdate={fetchPost}
            onPostDelete={() => navigate(`/profile/${postOwnerId}`)}
          />
        ) : null}
      </div>
    </div>
  );
};

export default PostPage;
